import React, { useEffect, useState } from 'react';
import { Avatar, Button, Card, Form, Input, Select, Space, message } from 'antd';
import { PictureOutlined, UserOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import AvatarCropper from '../../components/AvatarCropper/AvatarCropper';

const { TextArea } = Input;

const AnimationNpcForm = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);
  const [loading, setLoading] = useState(false);
  const [universes, setUniverses] = useState([]);
  const [avatarCropperOpen, setAvatarCropperOpen] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState(null);

  useEffect(() => {
    api.get('/api/universes').then((response) => {
      setUniverses(response.data?.universes || []);
    });
    if (isEditing) {
      api.get(`/api/admin/animation-npcs/${id}`).then((response) => {
        const npc = response.data;
        form.setFieldsValue({
          name: npc.name,
          universe_id: npc.universe_id,
          occupation: npc.occupation,
          biography: npc.biography,
          personality: npc.personality,
          dialogueStyle: npc.dialogueStyle,
          abilities: npc.abilities,
          secrets: npc.secrets,
          roleInStory: npc.roleInStory,
          relationships: npc.relationships,
          quests: npc.quests,
          appearance: npc.appearance,
        });
        if (npc.avatar) {
          setSelectedAvatar({ url: npc.avatar, originalFilename: 'Avatar actuel' });
        }
      }).catch(() => {
        message.error('PNJ introuvable');
        navigate('/animation-npcs');
      });
    }
  }, [form, id, isEditing, navigate]);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const payload = {
        ...values,
        avatar: selectedAvatar?.url || null,
      };

      if (isEditing) {
        await api.put(`/api/admin/animation-npcs/${id}`, payload);
        message.success('PNJ mis à jour');
        navigate(`/animation-npcs/detail/${id}`);
      } else {
        const response = await api.post('/api/admin/animation-npcs', payload);
        message.success('PNJ d\'animation créé');
        navigate(`/animation-npcs/detail/${response.data.id}`);
      }
    } catch (error) {
      message.error(error.response?.data?.error || 'Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Card title={isEditing ? 'Modifier le PNJ d\'animation' : 'Nouveau PNJ d\'animation'}>
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="name" label="Nom" rules={[{ required: true, message: 'Nom requis' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="universe_id" label="Univers" rules={[{ required: true, message: 'Univers requis' }]}>
            <Select
              disabled={isEditing}
              options={universes.map((u) => ({ value: u.id, label: u.name }))}
            />
          </Form.Item>

          <Form.Item label="Avatar">
            <div>
              {selectedAvatar ? (
                <div style={{ marginBottom: 16 }}>
                  <Avatar src={selectedAvatar.url} size={100} icon={<UserOutlined />} />
                  <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
                    {selectedAvatar.originalFilename}
                  </div>
                </div>
              ) : (
                <Avatar size={100} icon={<UserOutlined />} style={{ marginBottom: 8 }} />
              )}
              <Space>
                <Button icon={<PictureOutlined />} onClick={() => setAvatarCropperOpen(true)}>
                  {selectedAvatar ? 'Changer l\'avatar' : 'Sélectionner un avatar'}
                </Button>
                {selectedAvatar && (
                  <Button onClick={() => setSelectedAvatar(null)}>
                    Supprimer
                  </Button>
                )}
              </Space>
            </div>
          </Form.Item>

          <Form.Item name="occupation" label="Occupation"><Input /></Form.Item>
          <Form.Item name="biography" label="Biographie"><TextArea rows={4} /></Form.Item>
          <Form.Item name="personality" label="Personnalité"><TextArea rows={3} /></Form.Item>
          <Form.Item name="dialogueStyle" label="Style de dialogue"><TextArea rows={3} /></Form.Item>
          <Form.Item name="abilities" label="Capacités"><TextArea rows={3} /></Form.Item>
          <Form.Item name="secrets" label="Secrets (MJ)"><TextArea rows={3} /></Form.Item>
          <Form.Item name="roleInStory" label="Rôle dans l'histoire"><TextArea rows={2} /></Form.Item>
          <Form.Item name="relationships" label="Relations"><TextArea rows={2} /></Form.Item>
          <Form.Item name="quests" label="Quêtes / objectifs"><TextArea rows={2} /></Form.Item>
          <Form.Item name="appearance" label="Apparence"><TextArea rows={2} /></Form.Item>
          <Space>
            <Button onClick={() => navigate(isEditing ? `/animation-npcs/detail/${id}` : '/animation-npcs')}>
              Annuler
            </Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              {isEditing ? 'Enregistrer' : 'Créer'}
            </Button>
          </Space>
        </Form>
      </Card>

      <AvatarCropper
        open={avatarCropperOpen}
        onClose={() => setAvatarCropperOpen(false)}
        onSelect={(media) => {
          setSelectedAvatar(media);
        }}
        value={selectedAvatar}
      />
    </>
  );
};

export default AnimationNpcForm;
